import { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Loader2, Save, X, ShieldAlert } from "lucide-react";
import TiptapEditor from "@/components/TiptapEditor";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { LastChanged, apiError, fmtDateTime } from "./vendorUtils";

function NoteEditor({ initial, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [content, setContent] = useState(initial?.content || "");
  return (
    <Card className="border-primary/40" data-testid="vendor-note-editor">
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800" data-testid="vendor-note-password-hint">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          Bitte keine Passwörter oder Zugangsdaten in Notizen speichern.
        </div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel (optional)" data-testid="vendor-note-title" />
        <TiptapEditor content={content} onChange={setContent} placeholder="Notiz schreiben…" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="vendor-note-cancel"><X className="w-4 h-4 mr-1.5" />Abbrechen</Button>
          <Button onClick={() => onSave({ title, content })} disabled={saving} data-testid="vendor-note-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function VendorNotesTab({ vendorId, isAdmin, canEdit, onCount }) {
  const toast = useToast();
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await vendorsApi.listSub(vendorId, "notes");
    setNotes(res.data);
  }, [vendorId]);
  useEffect(() => { load().catch(() => toast.error("Notizen konnten nicht geladen werden")); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onCount?.(notes.length); }, [notes.length, onCount]);

  const save = async (data) => {
    if (!data.content.replace(/<[^>]*>/g, "").trim()) return toast.error("Die Notiz ist leer");
    setSaving(true);
    try {
      if (editing === "new") {
        const res = await vendorsApi.addItem(vendorId, "notes", data);
        setNotes((n) => [res.data, ...n]);
      } else {
        const res = await vendorsApi.updateItem(vendorId, "notes", editing, data);
        setNotes((n) => n.map((x) => (x.id === editing ? res.data : x)));
      }
      toast.success("Notiz gespeichert");
      setEditing(null);
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm("Diese Notiz wirklich löschen?")) return;
    try {
      await vendorsApi.deleteItem(vendorId, "notes", note.id);
      setNotes((n) => n.filter((x) => x.id !== note.id));
      toast.success("Notiz gelöscht");
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    }
  };

  return (
    <div className="space-y-3" data-testid="vendor-notes-list">
      {canEdit && <div className="flex justify-end">
        <Button onClick={() => setEditing("new")} disabled={editing === "new"} data-testid="vendor-note-add-btn"><Plus className="w-4 h-4 mr-1.5" />Neue Notiz</Button>
      </div>}
      {editing === "new" && <NoteEditor onSave={save} onCancel={() => setEditing(null)} saving={saving} />}
      {notes.length === 0 && editing !== "new" && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="vendor-notes-empty">Noch keine Notizen vorhanden.</p>}
      {notes.map((n) =>
        editing === n.id ? (
          <NoteEditor key={n.id} initial={n} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
        ) : (
          <Card key={n.id} data-testid={`vendor-note-${n.id}`}>
            <CardContent className="py-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {n.title && <p className="font-semibold">{n.title}</p>}
                  <p className="text-xs text-muted-foreground">Erstellt am {fmtDateTime(n.created_at)} von {n.created_by_name || "—"}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {canEdit && <Button variant="ghost" size="icon" onClick={() => setEditing(n.id)} title="Bearbeiten" data-testid={`vendor-note-edit-${n.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {isAdmin && <Button variant="ghost" size="icon" onClick={() => remove(n)} title="Löschen (nur Admin)" data-testid={`vendor-note-delete-${n.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                </div>
              </div>
              <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.content || "") }} />
              <LastChanged item={n} testId={`vendor-note-changed-${n.id}`} />
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
